import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { AlertTriangle, Check, Mail, UserMinus, UserPlus } from 'lucide-react'
import { Avatar, Badge, Spinner } from '@/components/ui'
import { useAnchoredPosition, useEscapeKey } from '@/hooks'
import { cn } from '@/internationalization/cn'
import { useT } from '@/internationalization/i18n'
import type { Assignee, AssigneeCandidate } from '@/types'
import { useAssigneeCandidates } from '../hooks'
import type { AssigneePayload } from '../api'

/**
 * Picks who owes an action item, including somebody who was never in the room.
 *
 * THREE GROUPS, IN THIS ORDER, AND THE ORDER IS THE ARGUMENT:
 *
 *  1. IN THIS MEETING — the roster. Almost always the right answer, because the
 *     commitment was made in front of these people.
 *  2. IN THE WORKSPACE — a colleague with a seat who was not on the invite.
 *     They get the item in their list and a notification, and they can open the
 *     meeting's TITLE but not its transcript. Marked "not in this meeting" so
 *     nobody assumes they heard it agreed.
 *  3. AN EMAIL ADDRESS — somebody at a patron, a vendor, a colleague with no
 *     seat. Typing one is a deliberate second step behind an explicit button,
 *     NOT a silent fallback when a search finds nothing: a typo would create
 *     a confident-looking assignment to nobody at all.
 *
 * What group 3 does NOT do is give that person access. No account is created,
 * no seat is consumed, and nothing is emailed to them by this action — they are
 * a label on the item, reachable through a Jira or Slack push. Bringing them
 * properly into the workspace is Settings › Members › Invite, which is
 * seat-aware and admin-gated, and doing it as a side effect of assigning a task
 * would hand somebody a recording because a colleague was tidying a list.
 */
export function AssigneePicker({
  meetingId,
  current,
  onSelect,
  disabled,
  align = 'start',
}: {
  meetingId: string
  current: Assignee | null
  onSelect: (assignee: AssigneePayload | null) => void
  disabled?: boolean
  align?: 'start' | 'end'
}) {
  const t = useT()
  const [isOpen, setIsOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [isTypingEmail, setIsTypingEmail] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const popoverRef = useRef<HTMLDivElement>(null)
  const close = useCallback(() => {
    setIsOpen(false)
    setQuery('')
    setIsTypingEmail(false)
  }, [])
  useEscapeKey(close, isOpen)

  // Portaled (see the position hook below), so a single `useClickOutside`
  // bound only to the trigger's own box would treat clicking inside the
  // panel itself as an outside click. Same pattern as `Dropdown`.
  useEffect(() => {
    if (!isOpen) return
    const onPointerDown = (event: PointerEvent) => {
      const target = event.target as Node
      if (containerRef.current?.contains(target)) return
      if (popoverRef.current?.contains(target)) return
      close()
    }
    // Capture, so a scroll on any ANCESTOR closes it -- the panel is anchored
    // to a rect that such a scroll invalidates. The candidate list inside the
    // popover is itself scrollable, though, and that scroll must not count --
    // it doesn't move the trigger, and closing on it would make a long list
    // impossible to read.
    const onScroll = (event: Event) => {
      const target = event.target
      if (target instanceof Node && popoverRef.current?.contains(target)) return
      close()
    }
    document.addEventListener('pointerdown', onPointerDown)
    window.addEventListener('scroll', onScroll, true)
    return () => {
      document.removeEventListener('pointerdown', onPointerDown)
      window.removeEventListener('scroll', onScroll, true)
    }
  }, [isOpen, close])

  // Was a plain `position: absolute` box under the trigger, which (a) had no
  // viewport awareness -- opened straight off the bottom edge when the
  // trigger sat near the fold -- and (b) was clipped outright by the Card
  // this sits inside (`overflow-hidden`). Anchored + portaled fixes both,
  // the same fix already shipped for `Dropdown`.
  const position = useAnchoredPosition(containerRef, popoverRef, {
    isOpen,
    side: 'bottom',
    align,
  })

  const { data, isPending, error } = useAssigneeCandidates(meetingId, isOpen)

  const { participants, members } = useMemo(() => {
    const term = query.trim().toLowerCase()
    const matches = (candidate: AssigneeCandidate) =>
      !term ||
      candidate.displayName.toLowerCase().includes(term) ||
      (candidate.email ?? '').toLowerCase().includes(term)
    const found = (data ?? []).filter(matches)
    return {
      participants: found.filter((c) => c.kind === 'participant'),
      members: found.filter((c) => c.kind === 'member'),
    }
  }, [data, query])

  // A search term that already looks like an address is offered directly, so
  // the common case (paste a vendor's email) is one step rather than two.
  const typedEmail = query.trim()
  const looksLikeEmail = /^[^@\s]+@[^@\s.]+(\.[^@\s.]+)+$/.test(typedEmail)

  const choose = (payload: AssigneePayload | null) => {
    onSelect(payload)
    close()
  }

  return (
    <div ref={containerRef} className="relative inline-block">
      <button
        type="button"
        disabled={disabled}
        onClick={() => setIsOpen((open) => !open)}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        className={cn(
          'inline-flex max-w-[16rem] items-center gap-1.5 rounded-md px-1.5 py-0.5 text-sm',
          'text-fg-muted hover:bg-surface-raised hover:text-fg',
          'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-brand',
          disabled && 'cursor-not-allowed opacity-60',
        )}
      >
        {current ? (
          <>
            <Avatar name={current.displayName} src={current.avatarUrl} size="xs" />
            <span className="truncate">{current.displayName}</span>
          </>
        ) : (
          <>
            <UserPlus aria-hidden className="h-3.5 w-3.5" />
            <span>{t('assignee.assign')}</span>
          </>
        )}
      </button>

      {isOpen &&
        createPortal(
          <div
            ref={popoverRef}
            role="listbox"
            aria-label={t('assignee.dialogLabel')}
            style={{ position: 'fixed', top: position?.top ?? 0, left: position?.left ?? 0 }}
            className={cn(
              'z-popover flex w-80 max-w-[calc(100vw-2rem)] flex-col overflow-hidden rounded-lg border border-border bg-surface shadow-lg',
              // Capped against the viewport rather than a flat value, so a tall
              // candidate list scrolls internally instead of the panel itself
              // running past the screen edge.
              'max-h-[min(24rem,calc(100vh-4rem))]',
              // Hidden until measured against the trigger and the viewport, so
              // it never flashes at (0, 0) before `useAnchoredPosition` places it.
              !position && 'invisible',
            )}
          >
          <div className="shrink-0 border-b border-border p-2">
            <input
              autoFocus
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder={t('assignee.searchPlaceholder')}
              aria-label={t('assignee.searchAria')}
              className="w-full rounded-md border border-border bg-surface-sunken px-2.5 py-1.5 text-sm text-fg placeholder:text-fg-subtle focus:outline focus:outline-2 focus:outline-brand"
            />
          </div>

          {/* The only scrolling region — the header above stays put. `min-h-0`
              is required for a flex child to shrink below its content size and
              actually scroll instead of overflowing its flex parent. */}
          <div className="min-h-0 flex-1 overflow-y-auto py-1">
            {isPending && (
              <p className="flex items-center gap-2 px-3 py-2 text-sm text-fg-muted">
                <Spinner /> {t('common.loadingPeople')}
              </p>
            )}
            {error && (
              <p className="px-3 py-2 text-sm text-danger">
                {t('assignee.loadFail')}
              </p>
            )}

            {participants.length > 0 && (
              <Group label={t('assignee.group.inThisMeeting')}>
                {participants.map((candidate) => (
                  <Row
                    key={candidate.id}
                    candidate={candidate}
                    isCurrent={current?.id === candidate.id}
                    onSelect={() => choose({ id: candidate.id })}
                  />
                ))}
              </Group>
            )}

            {members.length > 0 && (
              <Group label={t('assignee.group.inTheWorkspace')}>
                {members.map((candidate) => (
                  <Row
                    key={candidate.id}
                    candidate={candidate}
                    isCurrent={current?.id === candidate.id}
                    onSelect={() => choose({ id: candidate.id })}
                  />
                ))}
              </Group>
            )}

            {!isPending && participants.length === 0 && members.length === 0 && !looksLikeEmail && (
              <p className="px-3 py-2 text-sm text-fg-muted">
                {t('assignee.nobodyMatches', { query })}
              </p>
            )}

            {looksLikeEmail && (
              <Group label={t('assignee.group.outside')}>
                <button
                  type="button"
                  onClick={() => choose({ email: typedEmail })}
                  className="flex w-full items-start gap-2.5 px-3 py-2 text-left hover:bg-surface-raised"
                >
                  <Mail aria-hidden className="mt-0.5 h-4 w-4 shrink-0 text-fg-subtle" />
                  <span className="min-w-0">
                    <span className="block truncate text-sm text-fg">{typedEmail}</span>
                    <span className="block text-xs text-fg-muted">
                      {t('assignee.outsideNoAccess')}
                    </span>
                  </span>
                </button>
              </Group>
            )}

            {current && (
              <>
                <div className="my-1 border-t border-border" />
                <button
                  type="button"
                  onClick={() => choose(null)}
                  className="flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm text-fg-muted hover:bg-surface-raised hover:text-fg"
                >
                  <UserMinus aria-hidden className="h-4 w-4" />
                  {t('assignee.leaveUnassigned')}
                </button>
              </>
            )}

            {!isTypingEmail && !looksLikeEmail && (
              <>
                <div className="my-1 border-t border-border" />
                <button
                  type="button"
                  onClick={() => setIsTypingEmail(true)}
                  className="flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm text-fg-muted hover:bg-surface-raised hover:text-fg"
                >
                  <Mail aria-hidden className="h-4 w-4" />
                  {t('assignee.assignOutside')}
                </button>
              </>
            )}
            {isTypingEmail && !looksLikeEmail && (
              <p className="px-3 py-2 text-xs text-fg-muted">
                {t('assignee.typeInBox')}
              </p>
            )}
          </div>
          </div>,
          document.body,
        )}
    </div>
  )
}

function Group({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <p className="px-3 pb-1 pt-2 text-2xs font-semibold uppercase tracking-wide text-fg-subtle">
        {label}
      </p>
      {children}
    </div>
  )
}

function Row({
  candidate,
  isCurrent,
  onSelect,
}: {
  candidate: AssigneeCandidate
  isCurrent: boolean
  onSelect: () => void
}) {
  const t = useT()
  return (
    <button
      type="button"
      onClick={onSelect}
      className="flex w-full items-center gap-2.5 px-3 py-1.5 text-left hover:bg-surface-raised"
    >
      <Avatar name={candidate.displayName} src={candidate.avatarUrl} size="sm" />
      <span className="min-w-0 flex-1">
        <span className="block truncate text-sm text-fg">{candidate.displayName}</span>
        {candidate.email && (
          <span className="block truncate text-xs text-fg-subtle">{candidate.email}</span>
        )}
      </span>
      {!candidate.attendedMeeting && (
        <Badge tone="neutral" size="sm">
          {t('assignee.notInThisMeeting')}
        </Badge>
      )}
      {isCurrent && <Check aria-hidden className="h-4 w-4 shrink-0 text-brand" />}
    </button>
  )
}

/**
 * The assignee as rendered on an item, with what makes them unusual said out
 * loud. Shared by the list and the meeting detail so the two cannot disagree.
 */
export function AssigneeLabel({ assignee, inferred }: { assignee: Assignee | null; inferred: boolean }) {
  const t = useT()
  if (!assignee) {
    return (
      <Badge tone="warning" size="sm">
        {t('assignee.unassigned')}
      </Badge>
    )
  }
  return (
    <span className="inline-flex items-center gap-1.5">
      {assignee.displayName}
      {inferred && (
        <Badge tone="warning" size="sm">
          {t('assignee.inferred')}
        </Badge>
      )}
      {assignee.kind === 'external' && (
        <Badge tone="neutral" size="sm">
          {t('assignee.external')}
        </Badge>
      )}
      {assignee.kind === 'member' && (
        <span
          title={t('assignee.memberNotInMeetingTitle')}
          className="inline-flex items-center gap-1 text-xs text-fg-subtle"
        >
          <AlertTriangle aria-hidden className="h-3 w-3" />
          {t('assignee.notInThisMeeting')}
        </span>
      )}
    </span>
  )
}
